/* eslint-disable @typescript-eslint/no-unused-vars */
import { call, put, select, delay, takeLatest, take } from 'redux-saga/effects'
import { eventChannel, END } from 'redux-saga'
import store from 'src/store'
import { notify } from 'src/components/ui/notify/Notify'
import Toast from 'react-hot-toast'
import { createSagas } from 'src/helpers/redux'
import { IResponse } from 'src/services/storage/project/http'
import * as schemaService from 'src/services/storage/project/scheme'
import * as iconifyService from 'src/services/iconify'
import { realtimeService } from 'src/services/realtime'
import * as state from './state'
import { actions, Actions, PREFIX } from './actions'
import { setters } from './index'
import { selectors } from './selectors'
import { selectors as accountSelectors } from 'src/store/modules/account/selectors'

import { nanoid } from 'nanoid'
import * as agentsService from 'src/services/agents'
import { AxiosError } from 'axios'
import {
  CRMAgent,
  mastraClientAuth,
  mastraClentCancel,
} from 'src/services/agents/client'
import { tools } from 'src/services/agents/tools'
import * as app from 'src/store/modules/app'
import { snakeCase } from 'change-case'

const toastId = 'assistant/message'

const sagas = createSagas<Actions>(actions, {
  openWindow: function* ({ payload }) {
    yield put(setters.setWindow({ open: true }))
    const isStreaming: boolean = yield select(selectors.streamMessages)
    if (isStreaming) {
      yield put(
        app.actions.toastDismiss.pending({
          id: toastId,
        })
      )
    }
  },
  closeWidow: function* ({ payload }) {
    yield put(setters.setWindow({ open: false }))
  },
  toogleWindow: function* ({ payload }) {
    const isWindowOpen: boolean = yield select(selectors.windowOpen)
    const isStreaming: boolean = yield select(selectors.streamMessages)

    yield put(setters.setWindow({ open: !isWindowOpen }))

    if (!isWindowOpen && isStreaming) {
      yield put(
        app.actions.toastDismiss.pending({
          id: toastId,
        })
      )
    }
    if (isWindowOpen && isStreaming) {
      yield put(
        app.actions.tost.pending({
          id: toastId,
          type: 'progress',
          icon: 'mingcute:ai-fill',
          title: 'Assistant',
          description: 'Writes a message...',
        })
      )
    }
  },
  updateWindow: function* ({ payload }) {
    yield put(setters.setWindow(payload))
  },
  sendMessage: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)

    yield put(
      actions.streamMessage.pending({
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
        threadId: payload.threadId,
        messages: [
          {
            role: 'user',
            content: payload.text,
          },
        ],
      })
    )
  },

  getMemoryThreadsPaginated: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)
    const threads: IResponse<agentsService.IGetMemoryThreadsPaginatedData> =
      yield call(agentsService.getMemoryThreadsPaginated, {
        ...payload,
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
      })
    yield put(setters.setThreads(threads.data))

    const [defaultThread] = threads.data.threads

    if (!defaultThread) {
      const newDefaultThread: IResponse<agentsService.IPostMemoryThreadsData> =
        yield call(agentsService.postMemoryThreads, {
          resourceId: accountId.toString(),
          agentId: agentsService.CRM_AGENT_ID,
          threadId: `${
            agentsService.CRM_AGENT_ID
          }_${accountId.toString()}_default`,
          title: 'Default',
        })

      yield put(setters.setThreadsNewItem(newDefaultThread.data))
    }
  },
  postMemoryThreads: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)
    const thread: IResponse<agentsService.IPostMemoryThreadsData> = yield call(
      agentsService.postMemoryThreads,
      {
        ...payload,
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
      }
    )

    yield put(setters.setThreadsNewItem(thread.data))
  },
  patchMemoryThreadsItem: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)
    const thread: IResponse<agentsService.IPatchMemoryThreadsItemData> =
      yield call(agentsService.patchMemoryThreadsItem, {
        ...payload,
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
      })

    // yield put(setters.setThreadsUpdate(thread.data))
  },
  deleteMemoryThreadsItem: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)

    const thread: IResponse<agentsService.IDeleteMemoryThreadsItemData> =
      yield call(agentsService.deleteMemoryThreadsItem, {
        ...payload,
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
      })

    // yield put(setters.setThreadsRemove(payload))
  },
  getMemoryThreadsItemMessages: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)
    const threadMessges: IResponse<agentsService.IGetMemoryThreadsItemMessagesData> =
      yield call(agentsService.getMemoryThreadsItemMessages, {
        ...payload,
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
      })

    yield put(
      setters.setThreadsItemMessages({
        threadId: payload.threadId,
        messages: {
          items: threadMessges.data.uiMessages.reverse(),
        },
      })
    )
  },

  logging: {
    effect: (pattern, saga) => takeLatest('*', saga),
    controlled: true,
    saga: function* (data) {
      if (data.type.includes(PREFIX) && !data.type.includes('cacheStorage')) {
        yield put(actions.cacheStorage.pending())
      }
    },
  },
  cacheStorage: {
    effect: 'latest',
    controlled: true,
    saga: function* (data) {
      if (data.type.includes(PREFIX)) {
        yield delay(1000)
        try {
          const stateData: state.IStateData = yield select(selectors.data)
          localStorage.setItem(PREFIX, JSON.stringify(stateData))
        } catch (e) {
          console.debug(e)
        }
      }
    },
  },
  streamMessageCancel: function* ({ payload }) {
    yield put(setters.setStreamMessagesCancel(payload))
    try {
      mastraClentCancel()
    } catch (e) {}
  },
  streamMessage: function* ({ payload }) {
    try {
      mastraClientAuth()
      yield put(
        setters.setThreadsItemMessagesAppend({
          threadId: payload.threadId,
          messages: {
            items: payload.messages.map((message) => ({
              id: nanoid(),
              content: message.content,
              createdAt: new Date().toISOString(),
              role: 'user',
            })),
          },
        })
      )
      CRMAgent.stream({
        ...payload,
        //@ts-ignore
        clientTools: tools,
      })
        .then((stream) => {
          let assistantMessageId = ''
          let content = ''

          stream
            .processDataStream({
              onReasoningPart: (data) => {
                console.debug('onReasoningPart', data)
              },
              onDataPart: (data) => {
                console.debug('onDataPart', data)
              },
              onErrorPart: (data) => {
                console.debug('onErrorPart', data)
              },
              onFilePart: (data) => {
                console.debug('onFilePart', data)
              },
              onFinishMessagePart: (data) => {
                if (data.finishReason == 'stop') {
                  store.dispatch(
                    setters.setStreamMessagesComplited({
                      threadId: payload.threadId,
                      message: {},
                    })
                  )
                }
              },
              onFinishStepPart: (data) => {
                console.debug('onFinishStepPart', data)
              },
              onMessageAnnotationsPart: (data) => {
                console.debug('onMessageAnnotationsPart', data)
              },
              onReasoningSignaturePart: (data) => {
                console.debug('onReasoningSignaturePart', data)
              },
              onRedactedReasoningPart: (data) => {
                console.debug('onRedactedReasoningPart', data)
              },
              onSourcePart: (data) => {
                console.debug('onSourcePart', data)
              },
              onStartStepPart: (data) => {
                if (!assistantMessageId) {
                  assistantMessageId = data.messageId
                  if (assistantMessageId) {
                    store.dispatch(
                      setters.setStreamMessages({
                        threadId: payload.threadId,
                        message: {
                          id: assistantMessageId,
                          content: '',
                          createdAt: new Date().toISOString(),
                          parts: [],
                          role: 'assistant',
                        },
                      })
                    )
                  }
                }
              },
              onTextPart: (data) => {
                content += data
                store.dispatch(
                  setters.setStreamMessages({
                    threadId: payload.threadId,
                    message: {
                      id: assistantMessageId,
                      content: content,
                    },
                  })
                )
              },
              onToolCallDeltaPart: (data) => {
                console.debug('onToolCallDeltaPart', data)
              },
              onToolCallPart: (data) => {
                console.debug('onToolCallPart', data)
              },
              onToolCallStreamingStartPart: (data) => {
                console.debug('onToolCallStreamingStartPart', data)
              },
              onToolResultPart: (data) => {
                console.debug('onToolResultPart', data)
              },
            })
            .catch((error) => {
              if (error.name === 'AbortError') return
            })
        })
        .catch((error) => {
          if (error.name === 'AbortError') return
          console.debug('error', error)
        })
    } catch (e) {
      console.debug(e)
    }
  },
})

export default sagas
